import React, { useState } from 'react';
import AdminSidebar from '../../components/ui/AdminSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const GeneratedReportsManagement = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [reportTypeFilter, setReportTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('last30');
  const [selectedReports, setSelectedReports] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [reports, setReports] = useState([
    {
      id: 1,
      reportName: 'Monthly Revenue Analysis',
      reportType: 'Financial',
      generatedBy: 'System Auto',
      generatedDate: '2026-02-08 08:00:00',
      dateRange: 'Jan 2026',
      status: 'Completed',
      fileSize: '2.4 MB',
      format: 'PDF',
      downloadCount: 12,
      recipients: ['admin@company.com', 'finance@company.com'],
      description: 'Comprehensive monthly revenue breakdown with trend analysis'
    },
    {
      id: 2,
      reportName: 'Client Subscription Overview',
      reportType: 'Subscription',
      generatedBy: 'John Doe',
      generatedDate: '2026-02-07 14:30:00',
      dateRange: 'Q1 2026',
      status: 'Completed',
      fileSize: '1.8 MB',
      format: 'Excel',
      downloadCount: 8,
      recipients: ['admin@company.com'],
      description: 'Active subscriptions by plan type with churn analysis'
    },
    {
      id: 3,
      reportName: 'User Activity Audit Trail',
      reportType: 'Activity',
      generatedBy: 'Jane Smith',
      generatedDate: '2026-02-07 10:15:00',
      dateRange: 'Last 7 Days',
      status: 'Processing',
      fileSize: '5.2 MB',
      format: 'CSV',
      downloadCount: 0,
      recipients: ['security@company.com', 'admin@company.com'],
      description: 'Detailed user activity logs with security events'
    },
    {
      id: 4,
      reportName: 'Payment Transaction Summary',
      reportType: 'Financial',
      generatedBy: 'System Auto',
      generatedDate: '2026-02-06 23:59:00',
      dateRange: 'Jan 2026',
      status: 'Completed',
      fileSize: '3.1 MB',
      format: 'PDF',
      downloadCount: 15,
      recipients: ['finance@company.com'],
      description: 'All payment transactions with gateway reconciliation'
    },
    {
      id: 5,
      reportName: 'Module Usage Analytics',
      reportType: 'Analytics',
      generatedBy: 'Sarah Johnson',
      generatedDate: '2026-02-06 16:45:00',
      dateRange: 'Q4 2025',
      status: 'Failed',
      fileSize: '0 MB',
      format: 'Excel',
      downloadCount: 0,
      recipients: ['product@company.com'],
      description: 'Feature adoption and usage patterns across all modules',
      errorMessage: 'Data source connection timeout'
    },
    {
      id: 6,
      reportName: 'Refund Processing Report',
      reportType: 'Financial',
      generatedBy: 'System Auto',
      generatedDate: '2026-02-05 09:00:00',
      dateRange: 'Jan 2026',
      status: 'Completed',
      fileSize: '1.2 MB',
      format: 'PDF',
      downloadCount: 6,
      recipients: ['finance@company.com', 'support@company.com'],
      description: 'Refund requests processed with approval workflow details'
    },
    {
      id: 7,
      reportName: 'Client Onboarding Metrics',
      reportType: 'Subscription',
      generatedBy: 'Mike Wilson',
      generatedDate: '2026-02-04 11:20:00',
      dateRange: 'Q1 2026',
      status: 'Completed',
      fileSize: '900 KB',
      format: 'Excel',
      downloadCount: 4,
      recipients: ['sales@company.com'],
      description: 'New client acquisition and onboarding completion rates'
    },
    {
      id: 8,
      reportName: 'System Performance Audit',
      reportType: 'Technical',
      generatedBy: 'System Auto',
      generatedDate: '2026-02-03 00:00:00',
      dateRange: 'Last 30 Days',
      status: 'Completed',
      fileSize: '4.5 MB',
      format: 'PDF',
      downloadCount: 3,
      recipients: ['tech@company.com', 'admin@company.com'],
      description: 'Server uptime, response times, and error rate analysis'
    }
  ]);

  const filteredReports = reports?.filter(report => {
    const matchesSearch = report?.reportName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         report?.generatedBy?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesType = reportTypeFilter === 'all' || report?.reportType === reportTypeFilter;
    const matchesStatus = statusFilter === 'all' || report?.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  const totalPages = Math.ceil(filteredReports?.length / itemsPerPage);
  const paginatedReports = filteredReports?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSelectReport = (reportId) => {
    setSelectedReports(prev =>
      prev?.includes(reportId)
        ? prev?.filter(id => id !== reportId)
        : [...prev, reportId]
    );
  };

  const handleSelectAll = () => {
    if (selectedReports?.length === paginatedReports?.length) {
      setSelectedReports([]);
    } else {
      setSelectedReports(paginatedReports?.map(r => r?.id));
    }
  };

  const handleDownload = (report) => {
    console.log('Downloading report:', report?.reportName);
    setReports(prev =>
      prev?.map(r => r?.id === report?.id ? { ...r, downloadCount: r?.downloadCount + 1 } : r)
    );
  };

  const handleBulkDownload = () => {
    console.log('Bulk downloading reports:', selectedReports);
    setSelectedReports([]);
  };

  const handleBulkDelete = () => {
    if (window.confirm(`Delete ${selectedReports?.length} selected reports?`)) {
      setReports(prev => prev?.filter(r => !selectedReports?.includes(r?.id)));
      setSelectedReports([]);
    }
  };

  const handleExport = (format) => {
    console.log(`Exporting reports as ${format}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'text-emerald-600 bg-emerald-50';
      case 'Processing': return 'text-blue-600 bg-blue-50';
      case 'Failed': return 'text-red-600 bg-red-50';
      default: return 'text-slate-600 bg-slate-50';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Completed': return 'CheckCircle';
      case 'Processing': return 'Loader';
      case 'Failed': return 'XCircle';
      default: return 'Clock';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <AdminSidebar isCollapsed={sidebarCollapsed} />

      <div className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-64'}`}>
        {/* Header */}
        <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
          <div className="px-8 py-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-slate-900">Generated Reports</h1>
                <p className="text-slate-600 mt-1">View, download, and manage all system-generated reports</p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => console.log('Generate new report')}
                  className="bg-violet-600 hover:bg-violet-700 text-white px-6 py-2.5 rounded-lg font-medium flex items-center gap-2"
                >
                  <Icon name="Plus" className="w-5 h-5" />
                  Generate Report
                </Button>
              </div>
            </div>

            {/* Filters and Search */}
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[300px]">
                <div className="relative">
                  <Icon name="Search" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search by report name or creator..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  />
                </div>
              </div>

              <select
                value={reportTypeFilter}
                onChange={(e) => setReportTypeFilter(e?.target?.value)}
                className="px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                <option value="Financial">Financial</option>
                <option value="Subscription">Subscription</option>
                <option value="Activity">Activity</option>
                <option value="Analytics">Analytics</option>
                <option value="Technical">Technical</option>
              </select>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e?.target?.value)}
                className="px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="Completed">Completed</option>
                <option value="Processing">Processing</option>
                <option value="Failed">Failed</option>
              </select>

              <select
                value={dateRange}
                onChange={(e) => setDateRange(e?.target?.value)}
                className="px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              >
                <option value="last7">Last 7 Days</option>
                <option value="last30">Last 30 Days</option>
                <option value="last90">Last 90 Days</option>
                <option value="thisMonth">This Month</option>
                <option value="lastMonth">Last Month</option>
                <option value="thisQuarter">This Quarter</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedReports?.length > 0 && (
          <div className="bg-violet-50 border-b border-violet-200 px-8 py-4">
            <div className="flex items-center justify-between">
              <span className="text-violet-900 font-medium">
                {selectedReports?.length} report{selectedReports?.length !== 1 ? 's' : ''} selected
              </span>
              <div className="flex items-center gap-3">
                <Button
                  onClick={handleBulkDownload}
                  className="bg-white hover:bg-violet-50 text-violet-700 border border-violet-300 px-4 py-2 rounded-lg font-medium flex items-center gap-2"
                >
                  <Icon name="Download" className="w-4 h-4" />
                  Download Selected
                </Button>
                <Button
                  onClick={handleBulkDelete}
                  className="bg-white hover:bg-red-50 text-red-700 border border-red-300 px-4 py-2 rounded-lg font-medium flex items-center gap-2"
                >
                  <Icon name="Trash2" className="w-4 h-4" />
                  Delete Selected
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="p-8">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-600 text-sm font-medium">Total Reports</span>
                <Icon name="FileText" className="w-5 h-5 text-blue-600" />
              </div>
              <div className="text-3xl font-bold text-slate-900">{reports?.length}</div>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-600 text-sm font-medium">Completed</span>
                <Icon name="CheckCircle" className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="text-3xl font-bold text-slate-900">
                {reports?.filter(r => r?.status === 'Completed')?.length}
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-600 text-sm font-medium">Processing</span>
                <Icon name="Loader" className="w-5 h-5 text-blue-600" />
              </div>
              <div className="text-3xl font-bold text-slate-900">
                {reports?.filter(r => r?.status === 'Processing')?.length}
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-600 text-sm font-medium">Total Downloads</span>
                <Icon name="Download" className="w-5 h-5 text-violet-600" />
              </div>
              <div className="text-3xl font-bold text-slate-900">
                {reports?.reduce((sum, r) => sum + r?.downloadCount, 0)}
              </div>
            </div>
          </div>

          {/* Reports Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 text-left">
                      <input
                        type="checkbox"
                        checked={selectedReports?.length === paginatedReports?.length && paginatedReports?.length > 0}
                        onChange={handleSelectAll}
                        className="w-4 h-4 text-violet-600 rounded focus:ring-2 focus:ring-violet-500"
                      />
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Report Name</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Generated By</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Size</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Downloads</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {paginatedReports?.map((report) => (
                    <tr key={report?.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedReports?.includes(report?.id)}
                          onChange={() => handleSelectReport(report?.id)}
                          className="w-4 h-4 text-violet-600 rounded focus:ring-2 focus:ring-violet-500"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-slate-900">{report?.reportName}</div>
                          <div className="text-sm text-slate-500 mt-1">{report?.description}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                          {report?.reportType}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-slate-700">{report?.generatedBy}</td>
                      <td className="px-6 py-4">
                        <div className="text-slate-700">{new Date(report?.generatedDate)?.toLocaleDateString()}</div>
                        <div className="text-sm text-slate-500">{new Date(report?.generatedDate)?.toLocaleTimeString()}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(report?.status)}`}>
                          <Icon name={getStatusIcon(report?.status)} className="w-3.5 h-3.5" />
                          {report?.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-slate-700">{report?.fileSize}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-slate-700">
                          <Icon name="Download" className="w-4 h-4 text-slate-400" />
                          {report?.downloadCount}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {report?.status === 'Completed' && (
                            <button
                              onClick={() => handleDownload(report)}
                              className="p-2 hover:bg-violet-50 rounded-lg transition-colors group"
                              title="Download"
                            >
                              <Icon name="Download" className="w-4 h-4 text-slate-600 group-hover:text-violet-600" />
                            </button>
                          )}
                          <button
                            onClick={() => console.log('View details:', report)}
                            className="p-2 hover:bg-blue-50 rounded-lg transition-colors group"
                            title="View Details"
                          >
                            <Icon name="Eye" className="w-4 h-4 text-slate-600 group-hover:text-blue-600" />
                          </button>
                          <button
                            onClick={() => console.log('Share report:', report)}
                            className="p-2 hover:bg-emerald-50 rounded-lg transition-colors group"
                            title="Share"
                          >
                            <Icon name="Share2" className="w-4 h-4 text-slate-600 group-hover:text-emerald-600" />
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm('Delete this report?')) {
                                setReports(prev => prev?.filter(r => r?.id !== report?.id));
                              }
                            }}
                            className="p-2 hover:bg-red-50 rounded-lg transition-colors group"
                            title="Delete"
                          >
                            <Icon name="Trash2" className="w-4 h-4 text-slate-600 group-hover:text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
              <div className="text-sm text-slate-600">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredReports?.length)} of {filteredReports?.length} reports
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="ChevronLeft" className="w-4 h-4" />
                </Button>
                <span className="px-4 py-2 text-sm font-medium text-slate-700">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="ChevronRight" className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneratedReportsManagement;